import { Inbox, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Breadcrumb } from '@/components/common/Breadcrumb';
import { PageHeader } from '@/components/common/PageHeader';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { Can } from '@/components/permission/Can';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CodingJobsList } from '@/features/coder/components/CodingJobsList';
import { PendingPullRequestsBoard } from '@/features/coder/components/PendingPullRequestsBoard';
import { formatDate } from '@/features/common/format';
import { mutationErrorMessage } from '@/features/common/apiError';
import { useGetOrganizationQuery } from '@/features/orgs/api/orgApi';
import { PERMISSION_ACTIONS, PROJECT_RESOURCE } from '@/features/permissions/model';
import { useCan } from '@/features/permissions/useCan';
import { SubProjectsSection } from '@/features/reports/SubProjectsSection';
import {
  useDeleteResourceMutation,
  useGetProjectQuery,
  useGetResourcesQuery,
} from '../api/projectsApi';
import { CreateResourceDialog } from '../components/CreateResourceDialog';
import {
  RESOURCE_TYPE_ORDER,
  ResourceTypeBadge,
  resourceTypeLabel,
} from '../components/ResourceTypeBadge';
import type { ProjectResource, ProjectResourceType } from '../types';

const DELETE_DENIED_TOOLTIP =
  "You don't have permission to delete resources. Ask an organization owner or admin.";

/** The eight resource types, plus the “All types” entry (§12). */
const FILTER_ALL = 'all';

/**
 * Project detail — resources (design §12).
 *
 * `GET /projects/:id` for the header, `GET /projects/:id/resources?type=` for the
 * table; the type filter is a `?type=` search param so a filtered view is
 * deep-linkable (§12/F3). “Add resource” is hidden when `projects:attach` is
 * denied; the per-row delete stays visible but disabled with the reason, because
 * the actions column must not collapse (§14.5).
 *
 * Cross-feature panels mounted below the resources table: the reports
 * `SubProjectsSection` (P3-T9 host integration) and the coder
 * `PendingPullRequestsBoard` / `CodingJobsList` (P5-T6). Each renders its own
 * `<section>` and hides or shows an empty state internally, so they are
 * mounted unconditionally here.
 */
export function ProjectDetailPage() {
  const { orgId = '', projectId = '' } = useParams<{ orgId: string; projectId: string }>();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const typeParam = searchParams.get('type');
  const activeType =
    typeParam && (RESOURCE_TYPE_ORDER as string[]).includes(typeParam)
      ? (typeParam as ProjectResourceType)
      : FILTER_ALL;

  const projectQuery = useGetProjectQuery(projectId);
  const { data: organization } = useGetOrganizationQuery(orgId);
  const resourcesQuery = useGetResourcesQuery({ projectId, type: activeType });
  const canAdd = useCan(PERMISSION_ACTIONS.resourceAdd, PROJECT_RESOURCE);
  const canDelete = useCan(PERMISSION_ACTIONS.resourceRemove, PROJECT_RESOURCE);
  const [deleteTarget, setDeleteTarget] = useState<ProjectResource | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteResource, deleteState] = useDeleteResourceMutation();

  // A denied project must not keep its (empty) shell on screen.
  if (projectQuery.isError) {
    return (
      <QueryErrorView
        error={projectQuery.error}
        label="the project"
        onRetry={() => void projectQuery.refetch()}
      />
    );
  }

  const project = projectQuery.data;
  const resources = resourcesQuery.data ?? [];
  const addButton = (
    <Button onClick={() => setAddOpen(true)}>
      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
      Add resource
    </Button>
  );
  const addResourceAction = canAdd.allowed === true ? addButton : undefined;

  const columns: DataTableColumn<ProjectResource>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (resource) => (
        <span className="font-medium">
          {resource.name || (
            <span className="font-mono text-xs text-muted-foreground">{resource.refId}</span>
          )}
        </span>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (resource) => <ResourceTypeBadge type={resource.type} />,
    },
    {
      id: 'added',
      header: 'Added',
      cell: (resource) => (
        <span className="text-muted-foreground">
          {formatDate(resource.addedAt ?? resource.createdAt)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      srHeader: 'Actions',
      className: 'text-right',
      headerClassName: 'text-right',
      cell: (resource) => {
        const disabled = canDelete.allowed !== true;
        const button = (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            aria-label={`Delete ${resource.name || resource.refId}`}
            disabled={disabled}
            onClick={() => setDeleteTarget(resource)}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        );

        return disabled && canDelete.allowed === false ? (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Disabled buttons swallow pointer events; the span keeps hover alive. */}
              <span className="inline-flex">{button}</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{DELETE_DENIED_TOOLTIP}</TooltipContent>
          </Tooltip>
        ) : (
          button
        );
      },
    },
  ];

  function handleFilterChange(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === FILTER_ALL) {
      next.delete('type');
    } else {
      next.set('type', value);
    }
    setSearchParams(next, { replace: false });
  }

  const emptyState =
    activeType === FILTER_ALL ? (
      <EmptyState
        icon={<Inbox />}
        title="No resources"
        description="Attach a meeting, file, or other resource to this project."
        action={addResourceAction ?? undefined}
      />
    ) : (
      <EmptyState
        title={`No ${resourceTypeLabel(activeType)} resources`}
        description="Nothing here yet."
        action={addResourceAction ?? undefined}
      />
    );

  return (
    <div>
      <Breadcrumb
        className="mb-3"
        items={[
          { label: 'Home', to: '/' },
          { label: 'Organizations', to: '/organizations' },
          { label: organization?.name ?? '…', to: `/organizations/${orgId}/projects` },
          { label: 'Projects', to: `/organizations/${orgId}/projects` },
          { label: project?.name ?? '…', to: null },
        ]}
      />

      <PageHeader
        title={projectQuery.isLoading ? '…' : (project?.name ?? 'Project')}
        description={project?.description ?? `Created ${formatDate(project?.createdAt)}`}
      />

      <section aria-labelledby="resources-title" className="mt-6">
        <PageHeader
          title="Resources"
          titleAs="h2"
          titleId="resources-title"
          className="mb-4"
          actions={
            <>
              <Label htmlFor="resource-type-filter" className="sr-only">
                Type
              </Label>
              <Select value={activeType} onValueChange={handleFilterChange}>
                <SelectTrigger id="resource-type-filter" className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FILTER_ALL}>All types</SelectItem>
                  {RESOURCE_TYPE_ORDER.map((value) => (
                    <SelectItem key={value} value={value}>
                      {resourceTypeLabel(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Can action={PERMISSION_ACTIONS.resourceAdd} resource={PROJECT_RESOURCE}>
                {addButton}
              </Can>
            </>
          }
        />

        {resourcesQuery.isLoading ? (
          <LoadingRows label="Loading resources…" columns={4} />
        ) : resourcesQuery.isError ? (
          <QueryErrorView
            error={resourcesQuery.error}
            label="resources"
            onRetry={() => void resourcesQuery.refetch()}
          />
        ) : (
          <DataTable
            columns={columns}
            data={resources}
            pageSize={10}
            getRowId={(resource) => resource._id}
            emptyState={emptyState}
          />
        )}
      </section>

      <SubProjectsSection
        orgId={orgId}
        projectId={projectId}
        projectName={project?.name ?? 'this project'}
      />

      <PendingPullRequestsBoard projectId={projectId} />

      <CodingJobsList projectId={projectId} />

      <CreateResourceDialog open={addOpen} onOpenChange={setAddOpen} projectId={projectId} />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) {
            setDeleteTarget(null);
          }
        }}
        title="Delete resource?"
        description={
          deleteTarget
            ? `This permanently removes the ${resourceTypeLabel(
                deleteTarget.type,
              ).toLowerCase()} “${deleteTarget.name || deleteTarget.refId}” from the project.`
            : undefined
        }
        confirmLabel={deleteState.isLoading ? 'Deleting…' : 'Delete'}
        cancelLabel="Cancel"
        variant="destructive"
        isLoading={deleteState.isLoading}
        onConfirm={async () => {
          if (!deleteTarget) {
            return;
          }
          try {
            await deleteResource({ projectId, resourceId: deleteTarget._id }).unwrap();
            toast.success('Resource deleted.');
            setDeleteTarget(null);
          } catch (error) {
            toast.error(mutationErrorMessage(error, "Couldn't delete resource."));
          }
        }}
      />
    </div>
  );
}
